import { Box, Paper, Typography } from "@mui/material";
import { borderRadius, colors, elevation, spacing } from "../../theme";
import {
  DuplicateAuditListItem,
  DuplicateReviewLookups,
  SaveDuplicateAuditReviewRequest,
} from "../../types/duplicateAudit";
import {
  displayValue,
  formatAuditDateTime,
  formatDob,
  formatScore,
  getRisk,
  joinName,
} from "../../utils/duplicateAudit";
import ValueText from "./ValueText";
import FieldComparisonTable from "./FieldComparisonTable";
import ReviewForm, { ReviewFieldErrors } from "./ReviewForm";
import RiskBadge from "./RiskBadge";

interface Props {
  audit: DuplicateAuditListItem;
  lookups: DuplicateReviewLookups | null;
  saving: boolean;
  fieldErrors: ReviewFieldErrors;
  onSave: (request: SaveDuplicateAuditReviewRequest) => void;
}

interface CustomerCardProps {
  title: string;
  name: string;
  dob: string | null;
  postcode: string | null;
  email: string | null;
  phone: string | null;
  singleCustomerId: number | null;
  opticsSchemeId: number | null;
  hearingPatientId: number | null;
}

function CustomerCard(props: CustomerCardProps) {
  const ids = [
    { label: "Single Customer", value: props.singleCustomerId },
    { label: "Optics Scheme", value: props.opticsSchemeId },
    { label: "Hearing Patient", value: props.hearingPatientId },
  ];
  return (
    <Box
      sx={{
        border: `1px solid ${colors.secondary.light}`,
        borderRadius: borderRadius.mui.medium,
        p: spacing.mui.lg,
        bgcolor: colors.background.paper,
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        {props.title}
      </Typography>
      <Typography fontWeight={700} mb={spacing.mui.sm}>
        <ValueText value={props.name} />
      </Typography>
      <Typography variant="body2" component="div" lineHeight={1.8}>
        <div>DOB: <ValueText value={formatDob(props.dob)} /></div>
        <div>Postcode: <ValueText value={displayValue(props.postcode)} /></div>
        <div>Email: <ValueText value={displayValue(props.email)} /></div>
        <div>Phone: <ValueText value={displayValue(props.phone)} /></div>
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 1,
          mt: spacing.mui.md,
        }}
      >
        {ids.map((id) => (
          <Box key={id.label} sx={{ p: 1, bgcolor: colors.secondary.light, borderRadius: 1 }}>
            <Typography variant="caption" color={colors.text.secondary} display="block">
              {id.label}
            </Typography>
            <Typography variant="body2">
              <ValueText value={displayValue(id.value)} />
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function AuditInvestigationPanel({
  audit,
  lookups,
  saving,
  fieldErrors,
  onSave,
}: Props) {
  const risk = getRisk(audit.score);
  const newName = joinName(audit.customerFirstName, audit.customerLastName);

  return (
    <Paper
      elevation={elevation.low}
      sx={{ p: spacing.mui.lg, borderRadius: borderRadius.mui.medium }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {newName} review
          </Typography>
          <Typography variant="body2" color={colors.text.secondary}>
            Created {formatAuditDateTime(audit.createdAtUtc)} by {audit.createdBy}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RiskBadge score={audit.score} />
          <Typography
            variant="h5"
            fontWeight={700}
            color={risk === "Critical" ? colors.mui.error : colors.text.primary}
          >
            {formatScore(audit.score)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: spacing.mui.lg,
          mt: spacing.mui.lg,
        }}
      >
        <CustomerCard
          title="New Customer"
          name={newName}
          dob={audit.customerDateOfBirth}
          postcode={audit.customerPrimaryPostcode}
          email={audit.customerEmail}
          phone={audit.customerPrimaryPhone}
          singleCustomerId={audit.customerSingleCustomerId}
          opticsSchemeId={audit.customerOpticsSchemeId}
          hearingPatientId={audit.customerHearingPatientId}
        />
        <CustomerCard
          title="Potential Match"
          name={joinName(audit.matchingCustomerFirstName, audit.matchingCustomerLastName)}
          dob={audit.matchingCustomerDateOfBirth}
          postcode={audit.matchingCustomerPrimaryPostcode}
          email={audit.matchingCustomerEmail}
          phone={audit.matchingCustomerPrimaryPhone}
          singleCustomerId={audit.matchingCustomerSingleCustomerId}
          opticsSchemeId={audit.matchingCustomerOpticsSchemeId}
          hearingPatientId={audit.matchingCustomerHearingPatientId}
        />
      </Box>

      <Typography variant="h6" fontWeight={700} mt={spacing.mui.lg} mb={spacing.mui.sm}>
        Field Comparison
      </Typography>
      <FieldComparisonTable audit={audit} />

      <Box sx={{ mt: spacing.mui.lg }}>
        <ReviewForm
          audit={audit}
          lookups={lookups}
          saving={saving}
          fieldErrors={fieldErrors}
          onSave={onSave}
        />
      </Box>
    </Paper>
  );
}
